import { ApolloProvider as ApolloHooksProvider } from "@apollo/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@sentry/react";
import Logo from "assets/logos/tigerdata-logo.svg?react";
import { ChakraProvider } from "@chakra-ui/react";
import { ChakraProvider as ChakraProviderV3 } from "@chakra-ui/react-v3";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { StatsigProvider } from "@statsig/react-bindings";
import { RouterProvider } from "@tanstack/react-router";
import { Provider } from "mobx-react";

import { client } from "./api/client";
import { queryClient } from "./api/queryClient";
import { router } from "./router";
import { AuthProvider, useAuth } from "./routes/AuthProvider";
import { stores } from "./stores";
import { chakraTheme } from "theme";
import { system } from "theme-v3";
import { ThemePreferenceProvider } from "theme/ThemePreferenceProvider";
import { colors } from "theme/tokens/colors";
import { ENV } from "utils/hostEnv";
import { statsig } from "utils/statsig";

const InnerApp = () => {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
};

// Last-resort fallback for crashes above the router (providers, theme, router
// init). It can't use Chakra/theme/router — those may be what crashed — so it is
// intentionally dependency-free: inline styles with primitive token values and
// only the logo SVG (a plain asset import) for branding.
const FONT_FAMILY =
  "'Geist', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif";
const RootErrorFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      padding: "24px",
      textAlign: "center",
      fontFamily: FONT_FAMILY,
      color: colors.gray[50],
      background: colors.black,
    }}
  >
    <Logo height={30} />
    <h1 style={{ fontSize: "20px", fontWeight: 600, margin: 0 }}>
      Something went wrong
    </h1>
    <p
      style={{
        fontSize: "14px",
        color: colors.gray[500],
        maxWidth: "420px",
        margin: 0,
      }}
    >
      The console hit an unexpected error. Reloading usually fixes it — if it
      keeps happening, check our{" "}
      <a
        href="https://status.tigerdata.com/"
        style={{ color: colors.indigo[500] }}
      >
        status page
      </a>
      .
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        marginTop: "8px",
        padding: "10px 20px",
        borderRadius: "8px",
        border: "none",
        background: colors.indigo[500],
        color: colors.gray[50],
        fontFamily: FONT_FAMILY,
        fontSize: "14px",
        cursor: "pointer",
      }}
    >
      Reload
    </button>
  </div>
);

export const App = () => (
  <ErrorBoundary fallback={<RootErrorFallback />}>
    <GoogleOAuthProvider clientId={ENV.GOOGLE_CLIENT_ID}>
      <ApolloHooksProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <Provider {...stores}>
            <ChakraProvider theme={chakraTheme}>
              <ThemePreferenceProvider>
                <ChakraProviderV3 value={system}>
                  <StatsigProvider client={statsig}>
                    <AuthProvider>
                      <InnerApp />
                    </AuthProvider>
                  </StatsigProvider>
                </ChakraProviderV3>
              </ThemePreferenceProvider>
            </ChakraProvider>
          </Provider>
        </QueryClientProvider>
      </ApolloHooksProvider>
    </GoogleOAuthProvider>
  </ErrorBoundary>
);
